import { z } from 'zod';
import { persistedTimestampSchema } from './firestore-timestamp.schema.js';
/** Grandes étapes affichées dans la timeline client, moins fines que les statuts métier. */
export const shipmentPhaseSchema = z.enum([
    'order',
    'preparation',
    'dpd_collection',
    'collected_europe',
    'warehouse',
    'befret_transit',
    'congo_distribution',
    'delivered',
    'return_to_sender',
    'heavy_processing',
    'heavy_collection',
    'heavy_delivery',
    'incident',
    'vm_reception',
    'vm_unidentified',
]);
/** Statuts B2C persistés par Stripe, DPD, le backoffice et le parcours lourd. */
export const shipmentStatusSchema = z.enum([
    'order_created',
    'payment_pending',
    'payment_completed',
    'label_generated',
    'dpd_creation_in_progress',
    'dpd_creation_failed',
    'ready_for_deposit',
    'collection_scheduled',
    'deposited_at_pickup',
    'collected_by_dpd',
    'dpd_transit_eu',
    'dpd_customs_check',
    'dpd_incident_eu',
    'held_at_dpd_depot',
    'arriving_befret',
    'received_at_warehouse',
    'warehouse_processing',
    'warehouse_sorted',
    'warehouse_palletized',
    'ready_for_befret',
    'destination_sorted',
    'preparation_blocked',
    'befret_transit',
    'customs_clearance',
    'local_sorting',
    'delivery_sorted',
    'out_for_delivery',
    'delivery_attempted',
    'delivery_rescheduled',
    'awaiting_pickup',
    'delivered',
    'returned_to_sender',
    'return_received_at_warehouse',
    'return_completed',
    'lost',
    'damaged',
    'heavy_request_received',
    'heavy_analysis_in_progress',
    'heavy_quote_prepared',
    'heavy_quote_sent',
    'heavy_quote_accepted',
    'heavy_quote_rejected',
    'heavy_quote_paid',
    'heavy_specialist_assigned',
    'heavy_transport_planned',
    'heavy_collection_scheduled',
    'heavy_team_dispatched',
    'heavy_on_site_assessment',
    'heavy_loading_in_progress',
    'heavy_collected',
    'heavy_transit_to_warehouse',
    'heavy_delivery_scheduled',
    'heavy_delivery_team_assigned',
    'heavy_out_for_delivery',
    'heavy_delivery_attempted',
    'heavy_delivery_rescheduled',
    'heavy_delivered_with_installation',
    'vm_package_received',
    'shipping_payment_pending',
    'consolidation_payment_pending',
    'assisted_draft',
    'assisted_sms_sent',
    'assisted_expired',
    'assisted_cancelled',
    'cancelled',
    'poorly_packed',
    'dangerous_goods',
    'seized_customs_eu',
    'seized_customs_congo',
]);
export const SHIPMENT_STATUSES = shipmentStatusSchema.options;
export const SHIPMENT_PHASES = shipmentPhaseSchema.options;
/** Valeurs historiques uniquement tolérées à la lecture. */
export const SHIPMENT_STATUS_ALIASES = {
    draft: 'order_created',
    created: 'order_created',
    payment_processing: 'payment_pending',
    payment_failed: 'payment_pending',
    shipment_created: 'label_generated',
    dpd_shipment_created: 'label_generated',
    dpd_delivery_exception: 'dpd_incident_eu',
    warehouse_issue: 'preparation_blocked',
    customs_held: 'seized_customs_congo',
    customs_cleared: 'local_sorting',
    delivery_failed: 'delivery_attempted',
    heavy_parcel_pending: 'heavy_request_received',
    heavy_quote_payment_pending: 'heavy_quote_accepted',
    heavy_in_transit: 'heavy_transit_to_warehouse',
    heavy_at_warehouse: 'received_at_warehouse',
    heavy_delivered: 'heavy_delivered_with_installation',
    vm_received: 'vm_package_received',
};
export const SHIPMENT_PHASE_ALIASES = {
    dpd_phase: 'dpd_collection',
    befret_phase: 'befret_transit',
    order_confirmed: 'order',
    warehouse_befret: 'warehouse',
    shipped_to_congo: 'befret_transit',
    arrived_congo: 'congo_distribution',
    delivered_final: 'delivered',
    vm_consolidation: 'vm_reception',
};
export function normalizeShipmentStatus(value) {
    const parsed = shipmentStatusSchema.safeParse(value);
    if (parsed.success)
        return parsed.data;
    return typeof value === 'string'
        ? SHIPMENT_STATUS_ALIASES[value] ?? null
        : null;
}
export function normalizeShipmentPhase(value) {
    const parsed = shipmentPhaseSchema.safeParse(value);
    if (parsed.success)
        return parsed.data;
    return typeof value === 'string'
        ? SHIPMENT_PHASE_ALIASES[value] ?? null
        : null;
}
/** Entrée stable de la timeline client, moins détaillée que l’historique des statuts. */
export const shipmentTimelineEntrySchema = z
    .object({
    phase: shipmentPhaseSchema,
    label: z.string().min(1),
    description: z.string().min(1),
    completed: z.boolean(),
    completedAt: persistedTimestampSchema.optional(),
    estimatedAt: persistedTimestampSchema.optional(),
})
    .strict();
/**
 * État courant dénormalisé pour l’affichage. `phase` doit rester cohérente avec
 * `phase.current` ; cet invariant est contrôlé par le schéma lifecycle agrégé.
 */
export const shipmentStatusDetailsSchema = z
    .object({
    current: shipmentStatusSchema,
    phase: shipmentPhaseSchema,
    label: z.string().min(1),
    description: z.string().min(1),
    isTerminal: z.boolean(),
    nextActions: z.array(z.string()).optional(),
    updatedAt: persistedTimestampSchema.optional(),
})
    .strict();
/** Phase affichée, pourcentage global et timeline associée. */
export const shipmentPhaseProgressSchema = z
    .object({
    current: shipmentPhaseSchema,
    progress: z.number().min(0).max(100),
    timeline: z.array(shipmentTimelineEntrySchema),
})
    .strict();
/**
 * Événement immuable de l’historique : qui a changé le statut, quand, où et
 * par quel canal. Les métadonnées restent extensibles car chaque producteur y
 * ajoute son identifiant technique sans modifier le contrat principal.
 */
export const shipmentStatusHistoryEntrySchema = z
    .object({
    id: z.string().min(1),
    status: shipmentStatusSchema,
    label: z.string().optional(),
    timestamp: persistedTimestampSchema,
    operator: z.string().optional(),
    location: z
        .object({
        address: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        coordinates: z.object({ latitude: z.number(), longitude: z.number() }).strict().optional(),
        facility: z.string().optional(),
    })
        .strict()
        .nullable()
        .optional(),
    notes: z.string().optional(),
    automated: z.boolean(),
    source: z.enum(['system', 'dpd_api', 'specialist', 'webhook', 'manual', 'backoffice', 'admin']),
    metadata: z.record(z.unknown()).optional(),
})
    .strict();
/** Parcours client initial d’un colis standard, avant qu’une étape soit complétée. */
export const STANDARD_TIMELINE = [
    ['order', 'Commande confirmée', 'Paiement validé - Veuillez déposer votre colis'],
    [
        'collected_europe',
        'Collecté en Europe',
        'Colis déposé au point relais DPD et pris en charge pour transport',
    ],
    [
        'warehouse',
        'Arrivé chez BeFret Belgique',
        'Colis reçu et préparation pour expédition vers le Congo',
    ],
    ['befret_transit', 'Expédié vers le Congo', 'Transport international en cours vers la RDC'],
    ['congo_distribution', 'Arrivé au Congo', 'Colis arrivé au point relais BeFret local'],
    ['delivered', 'Livré', 'Colis livré au destinataire'],
];
/** Parcours client initial d’un colis lourd ; il rejoint ensuite le flux logistique commun. */
export const HEAVY_TIMELINE = [
    ['heavy_processing', 'Analyse de la demande', 'Analyse des besoins et préparation du devis'],
    ['heavy_collection', 'Collecte spécialisée', 'Organisation du transport spécialisé'],
    ['warehouse', 'Arrivé chez BeFret Belgique', 'Colis reçu et préparation pour expédition'],
    ['befret_transit', 'Expédié vers le Congo', 'Transport international en cours vers la RDC'],
    ['congo_distribution', 'Arrivé au Congo', 'Colis arrivé au point relais BeFret local'],
    ['delivered', 'Livré', 'Colis livré au destinataire'],
];
/** Crée une nouvelle timeline mutable sans partager les constantes du catalogue. */
export function createInitialTimeline(category) {
    const source = category === 'standard' ? STANDARD_TIMELINE : HEAVY_TIMELINE;
    return source.map(([phase, label, description]) => ({
        phase,
        label,
        description,
        completed: false,
    }));
}
